import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createAuthSession } from '../services/authSession';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const auth = useAuthState();
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const auth = useContext(AuthContext);
  if (!auth) throw new Error('useAuth must be used inside AuthProvider');
  return auth;
}

const IMPERSONATION_STORAGE_KEY = 'kcs-impersonation-email';
const DEV_AUTH_STORAGE_KEY = 'kcs-dev-auth-email';
const AC_EMPLOYEE_STORAGE_KEY = 'kcs-ac-employee-id';
const SUPER_ADMIN_EMAILS = new Set([
  'phil@knowknot.com',
  'phil.robinson@kirinari.com.au',
  'phil.robinson@livebetter.org.au',
  'jennifer.young@kirinari.com.au',
  'mark.janos@kirinari.com.au',
  'tom.gottwald@kirinari.com.au',
  'paul.murphy@kirinari.com.au',
  'jason.dawson@kirinari.com.au',
  'damian.meade@kirinari.com.au'
]);
const LOCAL_DEV_ADMIN_EMAILS = new Set([
  'admin@whiteglovespro.com'
]);
const MVP_ALLOWED_EMAILS = new Set([
  'phil@knowknot.com',
  'phil.robinson@kirinari.com.au',
  'phil.robinson@livebetter.org.au',
  'jennifer.young@kirinari.com.au',
  'mark.janos@kirinari.com.au',
  'tom.gottwald@kirinari.com.au',
  'paul.murphy@kirinari.com.au',
  'jason.dawson@kirinari.com.au',
  'damian.meade@kirinari.com.au'
]);

const isLocalhost = () => {
  if (typeof window === 'undefined') return false;
  return ['localhost', '127.0.0.1'].includes(window.location.hostname);
};

const normalizeEmail = (value) => {
  let email = String(value || '').trim().toLowerCase();
  // Handle Entra External/Guest UPN format: e.g. phil.robinson_kirinari.com.au#ext#@kirinari.onmicrosoft.com
  if (email.includes('#ext#')) {
    const prefix = email.split('#ext#')[0];
    const lastUnderscore = prefix.lastIndexOf('_');
    if (lastUnderscore !== -1) {
      email = prefix.substring(0, lastUnderscore) + '@' + prefix.substring(lastUnderscore + 1);
    }
  }
  return email;
};

const getClaimValues = (principal, claimTypes) => {
  const claims = principal?.claims || [];
  return claims.filter((item) => {
    const claimType = (item.typ || '').toLowerCase();
    return claimTypes.some((type) => claimType === type || claimType.endsWith(`/${type}`));
  }).map((item) => normalizeEmail(item.val)).filter(Boolean);
};

const getUserEmail = (principal) => {
  const claimEmails = getClaimValues(principal, ['email', 'emails', 'preferred_username', 'upn', 'emailaddress']);
  return claimEmails[0] || normalizeEmail(principal?.userDetails);
};

const getUserEmailCandidates = (principal) => [
  ...getClaimValues(principal, ['email', 'emails', 'preferred_username', 'upn', 'emailaddress']),
  normalizeEmail(principal?.userDetails),
].filter((email, index, emails) => email && emails.indexOf(email) === index);

const buildBasePrincipal = (email, workers = []) => {
  const normalizedEmail = normalizeEmail(email);
  const matchedWorker = workers.find((worker) => normalizeEmail(worker.email) === normalizedEmail);
  const matchedName = matchedWorker
    ? `${matchedWorker.firstName || ''} ${matchedWorker.lastName || ''}`.trim()
    : normalizedEmail;

  const roles = SUPER_ADMIN_EMAILS.has(normalizedEmail)
    ? ['admin', 'kcs-super-user']
    : LOCAL_DEV_ADMIN_EMAILS.has(normalizedEmail) || matchedWorker?.type === 'Admin'
      ? ['admin']
      : ['worker'];

  return {
    identityProvider: 'local-dev',
    userId: `dev-${normalizedEmail || 'unknown'}`,
    userDetails: matchedName,
    userRoles: roles,
    claims: [
      { typ: 'email', val: normalizedEmail },
      { typ: 'preferred_username', val: normalizedEmail },
      { typ: 'upn', val: normalizedEmail },
      { typ: 'name', val: matchedName },
    ],
  };
};

const buildImpersonatedPrincipal = (basePrincipal, worker) => {
  const fullName = `${worker.firstName || ''} ${worker.lastName || ''}`.trim();
  const email = normalizeEmail(worker.email);
  const filteredClaims = (basePrincipal?.claims || []).filter((claim) => {
    const claimType = (claim.typ || '').toLowerCase();
    return !['email', 'emails', 'preferred_username', 'upn', 'name'].includes(claimType);
  });

  return {
    ...(basePrincipal || {}),
    userId: `impersonated-${worker.id}`,
    userDetails: fullName,
    userRoles: worker.type === 'Admin' ? ['admin'] : ['worker'],
    claims: [
      ...filteredClaims,
      { typ: 'email', val: email },
      { typ: 'preferred_username', val: email },
      { typ: 'upn', val: email },
      { typ: 'name', val: fullName },
    ],
    impersonation: {
      workerId: worker.id,
      email,
      name: fullName,
      type: worker.type,
    },
  };
};

function useAuthState() {
  const [realUser, setRealUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authError, setAuthError] = useState(null);
  const session = useRef(null);
  const mounted = useRef(false);
  const [workers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [devAuthEmail, setDevAuthEmail] = useState(
    () => localStorage.getItem(DEV_AUTH_STORAGE_KEY) || 'phil@knowknot.com'
  );
  const [impersonatedEmail, setImpersonatedEmail] = useState(
    () => localStorage.getItem(IMPERSONATION_STORAGE_KEY) || ''
  );
  const [activeEmployeeId, setActiveEmployeeId] = useState(
    () => localStorage.getItem(AC_EMPLOYEE_STORAGE_KEY) || '167'
  );
  const contextRef = useRef({ activeEmployeeId, devAuthEmail });
  contextRef.current = { activeEmployeeId, devAuthEmail };

  useEffect(() => {
    mounted.current = true;
    const originalFetch = window.fetch;
    if (!session.current) {
      session.current = createAuthSession({
        nativeFetch: (...args) => originalFetch(...args),
        isLocal: isLocalhost(),
        getProviderId: () => contextRef.current.activeEmployeeId,
        getDevelopmentHeaders: () => ({
          'x-kcs-user-email': contextRef.current.devAuthEmail,
          'x-kcs-user-roles': buildBasePrincipal(contextRef.current.devAuthEmail).userRoles.join(','),
        }),
        onSession: ({ principal, profile: updatedProfile, expired }) => {
          if (!mounted.current) return;
          const updatedUser = principal && updatedProfile ? { ...principal, userRoles: updatedProfile.userRoles || [], kcsProfile: updatedProfile } : principal;
          setRealUser((current) => JSON.stringify(current) === JSON.stringify(updatedUser) ? current : updatedUser);
          setProfile(updatedProfile);
          setAuthError(null);
          if (!expired && updatedProfile?.alayacareEmployeeId && !updatedProfile.userRoles?.includes('kcs-super-user')) {
            const employeeId = String(updatedProfile.alayacareEmployeeId);
            contextRef.current.activeEmployeeId = employeeId;
            setActiveEmployeeId(employeeId);
            localStorage.setItem(AC_EMPLOYEE_STORAGE_KEY, employeeId);
          }
          setLoading(false);
        },
        onError: (error) => {
          if (mounted.current) { setAuthError(error.message); setLoading(false); }
        },
        onExpired: () => {
          if (mounted.current) {
            const destination = window.location.pathname.startsWith('//') ? '/provider/dashboard' : window.location.pathname + window.location.search;
            window.location.assign(`/.auth/login/ciam?post_login_redirect_uri=${encodeURIComponent(destination)}`);
          }
        },
      });
    }
    const apiFetch = session.current.fetchApi;
    window.fetch = apiFetch;
    const resume = () => {
      if (document.visibilityState !== 'hidden') session.current.refresh().catch(() => {});
    };
    window.addEventListener('focus', resume);
    window.addEventListener('online', resume);
    document.addEventListener('visibilitychange', resume);
    document.addEventListener('pointerdown', resume, true);
    session.current.refresh().catch(() => {});
    return () => {
      mounted.current = false;
      if (window.fetch === apiFetch) window.fetch = originalFetch;
      window.removeEventListener('focus', resume);
      window.removeEventListener('online', resume);
      document.removeEventListener('visibilitychange', resume);
      document.removeEventListener('pointerdown', resume, true);
    };
  }, []);

  const hasRealSession = useMemo(() => Boolean(realUser), [realUser]);
  const effectiveRealUser = useMemo(() => {
    if (realUser) return realUser;
    if (!isLocalhost() || !devAuthEmail) return null;
    return buildBasePrincipal(devAuthEmail, workers);
  }, [realUser, devAuthEmail, workers]);

  const realUserEmail = useMemo(() => getUserEmail(effectiveRealUser), [effectiveRealUser]);
  const isSuperAdmin = useMemo(
    () => getUserEmailCandidates(effectiveRealUser).some((email) => SUPER_ADMIN_EMAILS.has(email)),
    [effectiveRealUser]
  );
  const isAllowedUser = useMemo(() => {
    // If user has an active backend profile or is super admin or local dev, allow
    if (realUser?.kcsProfile || isSuperAdmin || !hasRealSession) return true;
    return getUserEmailCandidates(effectiveRealUser).some((email) => MVP_ALLOWED_EMAILS.has(email));
  }, [effectiveRealUser, realUser, isSuperAdmin, hasRealSession]);

  const user = useMemo(() => {
    if (!effectiveRealUser) return null;
    const baseUser = isSuperAdmin
      ? {
        ...effectiveRealUser,
        userRoles: [...new Set([...(effectiveRealUser.userRoles || []), 'admin', 'kcs-super-user'])],
      }
      : effectiveRealUser;

    if (!isSuperAdmin || !impersonatedEmail) return baseUser;

    const targetWorker = workers.find((worker) => normalizeEmail(worker.email) === normalizeEmail(impersonatedEmail));
    if (!targetWorker) return baseUser;

    return buildImpersonatedPrincipal(baseUser, targetWorker);
  }, [effectiveRealUser, workers, impersonatedEmail, isSuperAdmin]);

  const activeUserEmail = useMemo(() => getUserEmail(user), [user]);
  const activeUserRoles = useMemo(() => user?.userRoles || [], [user]);

  const switchEmployeeId = useCallback((employeeId) => {
    const idStr = String(employeeId || '').trim();
    setActiveEmployeeId(idStr);
    contextRef.current.activeEmployeeId = idStr;
    setProfile(null);
    setLoading(true);
    setAuthError(null);
    session.current?.refresh(true).catch(() => {});
    if (idStr) {
      localStorage.setItem(AC_EMPLOYEE_STORAGE_KEY, idStr);
    } else {
      localStorage.removeItem(AC_EMPLOYEE_STORAGE_KEY);
    }
  }, []);

  const switchContext = useCallback((email) => {
    const normalizedEmail = normalizeEmail(email);
    setImpersonatedEmail(normalizedEmail);

    if (normalizedEmail) {
      localStorage.setItem(IMPERSONATION_STORAGE_KEY, normalizedEmail);
    } else {
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    }
  }, []);

  const clearContext = useCallback(() => {
    setImpersonatedEmail('');
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  }, []);

  const signInForDevelopment = useCallback((email) => {
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) return;
    setDevAuthEmail(normalizedEmail);
    contextRef.current.devAuthEmail = normalizedEmail;
    session.current?.refresh(true).catch(() => {});
    localStorage.setItem(DEV_AUTH_STORAGE_KEY, normalizedEmail);
  }, []);

  const signOutLocalDevelopment = useCallback(() => {
    setDevAuthEmail('');
    localStorage.removeItem(DEV_AUTH_STORAGE_KEY);
    clearContext();
  }, [clearContext]);

  useEffect(() => {
    if (!loading && !isSuperAdmin && impersonatedEmail) {
      clearContext();
    }
  }, [loading, isSuperAdmin, impersonatedEmail, clearContext]);

  return {
    user,
    providerName: Number(profile?.alayacareEmployeeId) === Number(activeEmployeeId) ? profile?.providerName || null : null,
    providerConfigured: Number(profile?.alayacareEmployeeId) === Number(activeEmployeeId) ? profile?.providerConfigured : undefined,
    authError,
    refreshAuth: () => session.current?.refresh(true).catch(() => {}),
    realUser,
    workers,
    loading,
    hasRealSession,
    realUserEmail,
    activeUserEmail,
    activeUserRoles,
    activeEmployeeId,
    switchEmployeeId,
    isAllowedUser,
    isSuperAdmin,
    impersonatedEmail,
    switchContext,
    clearContext,
    isLocalDevMode: isLocalhost(),
    signInForDevelopment,
    signOutLocalDevelopment,
    loginUrls: {
      ciam: '/.auth/login/ciam',
    },
    logoutUrl: '/.auth/logout',
  };
}
